import { useCallback, useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useAuth } from '~/lib/auth'
import { listUserDpps, type DppRecord } from '~/lib/dpp'
import { AppHeader } from '~/components/app-header'
import { RequireAuth } from '~/components/require-auth'
import { Button } from '~/components/ui/button'

export const Route = createFileRoute('/dpp/')({
  head: () => ({
    meta: [
      { title: 'My Passports — Provenance' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  component: DppListPage,
})

function DppListPage() {
  return (
    <RequireAuth>
      <AppHeader />
      <DppList />
    </RequireAuth>
  )
}

function statusTone(status: string): string {
  switch (status) {
    case 'minted':
      return 'text-success border-success'
    case 'failed':
      return 'text-danger border-danger'
    default:
      return 'text-warning border-warning'
  }
}

function DppList() {
  const { user } = useAuth()
  const [dpps, setDpps] = useState<DppRecord[] | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError('')
    try {
      const data = await listUserDpps(user.email)
      setDpps(data)
    } catch {
      setError('Could not load your passports.')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <main className="mx-auto max-w-[1320px] px-[var(--gutter)] py-12 sm:py-16">
      <div className="mb-10 flex items-end justify-between gap-4 border-b border-[var(--line)] pb-6">
        <div>
          <div className="mb-2 font-mono text-[11px] tracking-[0.16em] uppercase text-accent">
            Provenance · Vault
          </div>
          <h1 className="font-mono text-[clamp(28px,3.6vw,40px)] font-[500] leading-[1.05] text-ink">
            My Passports
          </h1>
        </div>
        {dpps && dpps.length > 0 && (
          <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-ink-mute">
            {dpps.length} {dpps.length === 1 ? 'item' : 'items'}
          </span>
        )}
      </div>

      {loading && (
        <p className="py-20 text-center font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
          Loading…
        </p>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center gap-4 py-20">
          <p className="font-mono text-[12px] tracking-[0.06em] text-danger">
            {error}
          </p>
          <Button variant="outline" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && dpps && dpps.length === 0 && (
        <div className="flex flex-col items-center gap-5 py-20 text-center">
          <span className="watch-glyph" aria-hidden="true" />
          <p className="font-mono text-[12px] tracking-[0.12em] uppercase text-ink-mute">
            No passports found
          </p>
          <p className="max-w-[42ch] text-[14px] text-ink-soft">
            Passports issued to your email will appear here. Add a watch in the
            Provenance mobile app to get started.
          </p>
        </div>
      )}

      {!loading && !error && dpps && dpps.length > 0 && (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {dpps.map((d) => (
            <Link
              key={d.id}
              to="/dpp/$id"
              params={{ id: d.id }}
              className="bracket-frame relative flex flex-col gap-4 border border-[var(--line)] bg-surface p-6 transition-colors hover:border-ink-soft"
            >
              <span className="bk-bl" aria-hidden="true" />
              <span className="bk-br" aria-hidden="true" />

              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center border border-[var(--line)] bg-surface-2">
                  {d.thumbnail?.url ? (
                    <img
                      src={d.thumbnail.url}
                      alt={`${d.brand} ${d.model}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="watch-glyph" aria-hidden="true" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-[16px] font-[500] text-ink">
                    {d.brand}
                  </div>
                  <div className="truncate text-[14px] text-ink-soft">
                    {d.model}
                  </div>
                  <div className="mt-1 truncate font-mono text-[10px] tracking-[0.12em] uppercase text-ink-mute">
                    S/N {d.serialNumber}
                  </div>
                </div>
              </div>

              <div className="mt-auto flex items-center justify-between border-t border-[var(--line)] pt-4">
                <span
                  className={`inline-flex items-center border px-2 py-1 font-mono text-[9px] tracking-[0.14em] uppercase ${statusTone(d.status)}`}
                >
                  {d.status}
                </span>
                <span className="font-mono text-[14px] text-ink-mute">↗</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  )
}
